import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import '../styles/Login.css';
import '../styles/DropzoneLogin.css'; // Import the new theme (will override Login.css styles)
// Import icons
import { FcGoogle } from 'react-icons/fc';
import { MdEmail, MdLock, MdPerson, MdBusiness, MdPhone, MdWarning } from 'react-icons/md';
// Import email validation utility
import { validateBusinessEmail, validateGoogleOAuthEmail, getBusinessEmailSuggestion } from '../utils/emailValidation';

const Login = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showGoogleProfileForm, setShowGoogleProfileForm] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
    setEmailError('');
  };

  // Email validation handler
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Clear previous errors
    setEmailError('');
    setError('');
    
    // Validate email domain if email is not empty
    if (newEmail.trim()) {
      const validation = validateBusinessEmail(newEmail);
      if (!validation.isValid) {
        setEmailError(validation.message);
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    // Validate business email before attempting login
    const emailValidation = validateBusinessEmail(email);
    if (!emailValidation.isValid) {
      setEmailError(emailValidation.message);
      setError('Please use a business email address to sign in.');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setEmailError('');
      
      await signInWithEmailAndPassword(auth, email, password);
      // Successful login will automatically redirect via auth state change in App.js
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific Firebase auth errors
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address. Please check your email or contact your administrator.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later.');
      } else {
        setError(error.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      setEmailError('');
      
      const provider = new GoogleAuthProvider();
      
      // Add additional OAuth parameters for business accounts
      provider.setCustomParameters({
        'prompt': 'select_account',
        'hd': '' // This prompts for hosted domain but doesn't restrict
      });
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      console.log('Google OAuth user email received:', user.email); // Debug log
      
      // IMMEDIATE GMAIL CHECK - FIRST BARRIER
      if (!user.email) {
        console.log('No email received from Google OAuth');
        await auth.signOut();
        setError('No email address received from Google. Please try again.');
        setLoading(false);
        return;
      }
      
      const emailLower = user.email.toLowerCase().trim();
      console.log('Checking email:', emailLower);
      
      // STRICT GMAIL BLOCKING - SECOND BARRIER
      if (emailLower.includes('@gmail.') || emailLower.includes('@googlemail.')) {
        console.log('BLOCKING: Gmail/Googlemail detected:', emailLower);
        await auth.signOut();
        setGoogleUser(null);
        setShowGoogleProfileForm(false);
        setEmailError('Gmail personal accounts are completely blocked.');
        setError('❌ Personal Gmail accounts are not allowed. Please use your business Google Workspace account with your company domain (e.g., yourname@yourcompany.com).');
        setLoading(false);
        return;
      }
      
      // COMPREHENSIVE PERSONAL EMAIL CHECK - THIRD BARRIER
      const personalDomains = [
        'gmail.com', 'googlemail.com', 'gmail.co.uk', 'googlemail.co.uk',
        'hotmail.com', 'outlook.com', 'live.com', 'msn.com',
        'yahoo.com', 'yahoo.co.uk', 'ymail.com', 'rocketmail.com',
        'aol.com', 'aim.com', 'icloud.com', 'me.com', 'mac.com',
        'protonmail.com', 'tutanota.com'
      ];
      
      const domain = emailLower.split('@')[1];
      if (personalDomains.includes(domain)) {
        console.log('BLOCKING: Personal email domain detected:', domain);
        await auth.signOut();
        setGoogleUser(null);
        setShowGoogleProfileForm(false);
        setEmailError(`Personal email domain (${domain}) is not allowed.`);
        setError(`❌ ${domain} is a personal email provider. Please use your business email address.`);
        setLoading(false);
        return;
      }
      
      // ENHANCED VALIDATION - FOURTH BARRIER
      const emailValidation = validateGoogleOAuthEmail(user.email);
      if (!emailValidation.isValid) {
        console.log('BLOCKING: Failed validation:', user.email, emailValidation.message);
        
        // Sign out the user immediately to prevent any access
        await auth.signOut();
        
        // Clear any user state
        setGoogleUser(null);
        setShowGoogleProfileForm(false);
        
        // Set specific error messages
        setEmailError(emailValidation.message);
        setError('❌ Personal email addresses are not allowed. Please use your business email address.');
        setLoading(false);
        return;
      }
      
      // DISPOSABLE EMAIL CHECK - FIFTH BARRIER
      const disposableDomains = ['tempmail', 'temp-mail', '10minute', 'mailinator', 'guerrilla', 'throwaway'];
      if (disposableDomains.some(temp => domain.includes(temp))) {
        console.log('BLOCKING: Disposable email detected:', domain);
        await auth.signOut();
        setGoogleUser(null);
        setShowGoogleProfileForm(false);
        setEmailError('Temporary email addresses are not allowed.');
        setError('❌ Disposable email addresses are not permitted. Please use your business email address.');
        setLoading(false);
        return;
      }
      
      // SUSPICIOUS PATTERN CHECK - SIXTH BARRIER
      if (domain.length < 4 || !domain.includes('.') || domain.includes('test') || domain.includes('example')) {
        console.log('BLOCKING: Suspicious domain pattern:', domain);
        await auth.signOut();
        setGoogleUser(null);
        setShowGoogleProfileForm(false);
        setEmailError('Invalid email domain detected.');
        setError('❌ Please use a valid business email address.');
        setLoading(false);
        return;
      }
      
      console.log('✅ Business email validation passed:', user.email);
      
      // Check if user profile exists in Firestore
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // FINAL VALIDATION BEFORE PROFILE FORM
        console.log('No existing profile, showing profile form for:', user.email);
        setGoogleUser(user);
        setEmail(user.email || '');
        setFullName(user.displayName || '');
        setShowGoogleProfileForm(true);
        setLoading(false);
      } else {
        console.log('Existing profile found, redirecting to dashboard for:', user.email);
      }
      // If profile exists, auth state change will redirect in App.js
    } catch (error) {
      console.error('Google sign-in error:', error);
      
      // Enhanced error handling
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Pop-up was blocked by your browser. Please allow pop-ups and try again.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError('Multiple sign-in attempts detected. Please try again.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        setError('This domain is not authorized for Google sign-in. Please contact support.');
      } else {
        setError(error.message || 'Failed to sign in with Google. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleGoogleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!fullName || !company || !phone) {
      setError('Please fill in all fields');
      return;
    }
    
    // ADDITIONAL VALIDATION BEFORE PROFILE CREATION
    if (!googleUser || !googleUser.email) {
      setError('Invalid user session. Please sign in again.');
      return;
    }
    
    // RE-VALIDATE EMAIL BEFORE CREATING PROFILE
    const emailLower = googleUser.email.toLowerCase();
    if (emailLower.includes('@gmail.') || emailLower.includes('@googlemail.')) {
      console.log('BLOCKING: Gmail detected during profile creation:', googleUser.email);
      await auth.signOut();
      setGoogleUser(null);
      setShowGoogleProfileForm(false);
      setError('❌ Gmail accounts are not allowed. Profile creation blocked.');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      console.log('Creating profile for validated business email:', googleUser.email);
      
      // Store user profile in Firestore
      await setDoc(doc(db, "users", googleUser.uid), {
        fullName,
        company,
        phone,
        email: googleUser.email,
        createdAt: new Date(),
        emailDomain: googleUser.email.split('@')[1],
        accountType: 'business',
        validatedAt: new Date(),
        authMethod: 'google_oauth'
      });
      
      console.log('✅ Business profile created successfully for:', googleUser.email);
      setShowGoogleProfileForm(false);
      // Auth state change will redirect in App.js
    } catch (error) {
      setError(error.message || 'Failed to create profile');
      console.error('Profile creation error:', error);
    } finally {
      setLoading(false);
    }
  };

  // If showing Google profile form
  if (showGoogleProfileForm) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="login-logo">
              <img 
                src="/socfirstlogo.png" 
                alt="SOCFirst Logo" 
                className="logo-image"
                style={{
                  width: '80px',
                  height: '80px',
                  objectFit: 'contain'
                }}
              />
            </div>
            <h1>Complete Your Profile</h1>
            <p>Please provide additional information to complete your registration</p>
          </div>
          
          {error && <div className="login-error">{error}</div>}
          
          <form className="login-form" onSubmit={handleGoogleProfileSubmit}>
            <div className="form-group">
              <label htmlFor="full-name">
                <MdPerson className="input-icon" />
                Full Name
              </label>
              <input
                type="text"
                id="full-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">
                <MdEmail className="input-icon" />
                Business Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                readOnly
                disabled
                className="disabled-input"
              />
              <small className="email-verified">✓ Business email verified</small>
            </div>
            <div className="form-group">
              <label htmlFor="company">
                <MdBusiness className="input-icon" />
                Company
              </label>
              <input
                type="text"
                id="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Enter your company name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">
                <MdPhone className="input-icon" />
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your phone number"
                required
              />
            </div>
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Saving Profile...' : 'Complete Registration'}
            </button>
          </form>
          
          <div className="login-footer">
            <p>© {new Date().getFullYear()} SOCFirst AI. All rights reserved.</p>
            <div className="footer-links">
              <a 
                href="https://socfirst.com/privacy-policy/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer-link"
              >
                Privacy Policy
              </a>
              <span className="footer-separator">|</span>
              <a 
                href="https://socfirst.com/terms-of-use/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer-link"
              >
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img 
              src="/socfirstlogo.png" 
              alt="SOCFirst Logo" 
              className="logo-image"
              style={{
                width: '80px',
                height: '80px',
                objectFit: 'contain'
              }}
            />
          </div>
          <h1>SOCFirst AI Platform</h1>
          <p>Advanced Security Operations Center with AI capabilities</p>
        </div>
        
        {/* Hide the tabs section - only show login */}
        <div className="login-tabs" style={{ display: 'none' }}>
          <div 
            className={`login-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => handleTabChange('login')}
          >
            Login
          </div>
          <div 
            className={`login-tab ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => handleTabChange('register')}
          >
            Create Account
          </div>
        </div>
        
        {error && <div className="login-error">{error}</div>}
        {emailError && (
          <div className="login-error">
            <MdWarning style={{ marginRight: '8px', fontSize: '18px' }} />
            {emailError}
            <div className="email-suggestion">
              {getBusinessEmailSuggestion()}
            </div>
          </div>
        )}
        
        {/* Only show login form */}
        <form className="login-form" onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="email">
              <MdEmail className="input-icon" />
              Business Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your business email"
              required
              className={emailError ? 'input-error' : ''}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">
              <MdLock className="input-icon" />
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>
          
          <button 
            type="submit" 
            className="login-button" 
            disabled={loading || emailError}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          
          <div className="separator">
            <span>OR</span>
          </div>
          
          <button 
            type="button" 
            className="google-button" 
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <FcGoogle className="google-icon" />
            Sign in with Business Google Account
          </button>
        </form>
        
        <div className="business-email-info" style={{ display: 'none' }}>
          <p>
            <MdWarning style={{ marginRight: '5px', color: '#ff9800' }} />
            Only business email addresses are allowed. Personal emails (Gmail, Yahoo, Hotmail, etc.) cannot be used.
          </p>
        </div>
        
        <div className="login-footer">
          <p>© {new Date().getFullYear()} SOCFirst AI. All rights reserved.</p>
          <div className="footer-links">
            <a 
              href="https://socfirst.com/privacy-policy/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link"
            >
              Privacy Policy
            </a>
            <span className="footer-separator">|</span>
            <a 
              href="https://socfirst.com/terms-of-use/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="footer-link"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 